import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { auth, functions } from '../firebase';
import './UserImpersonationModal.css';

const UserImpersonationModal = ({ isOpen, onClose }) => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const getUsersFunc = httpsCallable(functions, 'getUsersForImpersonation');
      const result = await getUsersFunc({ searchQuery, limit: 50 });

      if (result.data.success) {
        setUsers(result.data.users);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadUsers();
  };

  const handleImpersonate = async (user) => {
    if (isImpersonating) return;

    const confirmMsg = `Are you sure you want to impersonate user:\n\n${user.email || user.uid}?\n\nAll actions will be logged.`;
    if (!window.confirm(confirmMsg)) return;

    setIsImpersonating(true);
    setSelectedUser(user);

    try {
      const impersonateFunc = httpsCallable(functions, 'impersonateUser');
      const result = await impersonateFunc({ targetUserId: user.uid });

      if (result.data.success) {
        // Store impersonation data
        localStorage.setItem('impersonationData', JSON.stringify({
          isImpersonating: true,
          targetUser: result.data.targetUser,
          startTime: Date.now(),
        }));

        // Sign in with the custom token
        await signInWithCustomToken(auth, result.data.customToken);

        // Close modal and reload
        onClose();
        window.location.reload();
      }
    } catch (error) {
      console.error('Error starting impersonation:', error);
      alert(`Failed to impersonate user: ${error.message}`);
    } finally {
      setIsImpersonating(false);
      setSelectedUser(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="impersonation-modal-overlay" onClick={onClose}>
      <div className="impersonation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="impersonation-modal-header">
          <h2>Impersonate User</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="impersonation-modal-warning">
          ⚠️ <strong>Warning:</strong> You are about to impersonate another user. All actions will be logged for security purposes.
        </div>

        <form onSubmit={handleSearch} className="impersonation-search">
          <input
            type="text"
            placeholder="Search by email, name, UID, or Strava athlete ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-btn" disabled={isLoading}>
            {isLoading ? 'Searching...' : 'Search'}
          </button>
        </form>

        <div className="users-list">
          {isLoading ? (
            <div className="loading">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="no-users">No users found</div>
          ) : (
            <div className="users-grid">
              {users.map((user) => (
                <div key={user.uid} className="user-card">
                  <div className="user-info">
                    {user.photoURL && (
                      <img src={user.photoURL} alt="" className="user-avatar" />
                    )}
                    <div className="user-details">
                      <div className="user-name">
                        {user.displayName || 'No Name'}
                      </div>
                      <div className="user-email">{user.email || 'No Email'}</div>
                      <div className="user-meta">
                        <span title={user.uid}>UID: {user.uid.substring(0, 8)}...</span>
                        {user.stravaAthleteId && <span className="strava-id" title="Strava Athlete ID">Strava ID: {user.stravaAthleteId}</span>}
                        {user.stravaConnected && <span className="strava-badge">Strava</span>}
                      </div>
                    </div>
                  </div>
                  <button
                    className="impersonate-btn"
                    onClick={() => handleImpersonate(user)}
                    disabled={isImpersonating && selectedUser?.uid === user.uid}
                  >
                    {isImpersonating && selectedUser?.uid === user.uid
                      ? 'Impersonating...'
                      : 'Impersonate'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserImpersonationModal;